import { touchRow } from './_touchTarget.js'

// The shared field kit — ONE field language for every workspace form (a
// modal, a sheet, a settings panel). Canonical replacement for the per-app
// `<AppAbbrev>Input`/`<AppAbbrev>Select` forks (ComSelect, SchSelect,
// CrmSelect, HrSelect, WhInput, BiSelect, JobSelect, HmInput, FinInput,
// CmsInput — see tickets/workspace.md's a11y ticket) and for the raw
// `tag: 'input'` blocks the modals carried with their own look each.
//
// THE RECIPE (Nika 2026-09-24, "improve fields ui/ux in modals, just like
// we have in aihouse" — the AI House admin sheets are the reference):
//   label    a small uppercase caption above the control — `Y1` (12.7px
//            at the 17px base, the AI House size; `Z3` is not a step of the
//            brand type scale and fell back to `Z`), 500, `.06em`, caption
//            ink (the weight law: labels are 500)
//   control  `controlH` tall, `radiusControl`, `Z2` type, `- Z2` inset
//            (DESIGN_LANGUAGE §6.2, the "Input / select / textarea" row),
//            on the brand `field` fill with a hairline `line` edge — the one
//            stroke the borderless law keeps (§2.5.4) — that steps to
//            `inactive` under the pointer; the quiet `placeholder` ink; the
//            global focus ring and nothing else; the `field-disabled` pair
//   notes    two optional lines under the control: a hint in caption ink
//            and an inline error in danger ink whose box can be reserved
//            (Rule 66: field validation → inline, box reserved)
//   select   a native <select> that KEEPS THE BROWSER'S OWN CARET (decided
//            2026-09-24, the AI House AhSelect precedent): no
//            `appearance: none`, no drawn chevron. The native control brings
//            the OS picker, type-ahead, the arrow keys and the accessible
//            name; its caret sits inside the `- Z2` inset, needs no reserved
//            box and follows the page's colour scheme (measured on AhSelect,
//            2026-09-20). A drawn chevron costs an Icon overlay, a reserved
//            inset per control size and a second pointer target that must
//            opt out of clicks — three more parts for the same signal.
//
// The family (this file unless named):
//   AppField                     the <label> wrapper: caption, hint, error
//   AppFieldLabel                the caption itself — Y1 / 500 / uppercase /
//                                .06em, caption ink; AppField's FieldLabel
//                                extends it, and a caption with no control
//                                of its own extends it by name
//   AppFieldHint / AppFieldError the two note lines under a control
//   AppInput / AppSelect /       the controls — controlH, Z2
//     AppTextarea
//   AppInputCompact /            the dense-row controls — controlHCompact, Z
//     AppSelectCompact           (a drawer row, a table's inline editor)
//   AppInputGroup                ONE field surface around a bare input
//                                (AppInputBare) and a fixed adornment:
//     AppInputPrefix             a fixed text before the input — a host
//                                before a slug, a sign before an amount
//     AppInputWithIcon           a leading glyph — a search field's magnifier
//   AppFieldPrefix               AppField + AppInputPrefix
//   The choice fields — a short option set is a choice control, never a
//   native select (their own files):
//     AppSegmented /             one line of segments, the brand TabSet
//       AppFieldSegmented        (AppFieldSegmented.js)
//     AppChoiceChip /            a wrapping row of choice chips, for a narrow
//       AppChoiceChips /         column (AppFieldChips.js)
//       AppFieldChips
//     AppColorChip /             colour options — a dot that IS the value
//       AppColorChips /          (AppFieldColors.js)
//       AppFieldColors
//
// Usage:
//
//   AppField: {
//     label: 'Person',                    // or (el) => el.call('polyglot', 'key')
//     hint: 'Shown on the invoice',       // optional, under the control
//     reserveError: true,                 // keep the error line's box even when empty
//     AppSelect: { value: ..., onChange: ..., children: [...] },
//     FieldError: { text: (el, s) => s.root.formErrors.person || '' }
//   }
//
// The two note lines are declared here, before the consumer's control key
// merges in, so flex `order` places them under the control. A reactive error
// is the consumer's own `FieldError.text` factory (a function-valued `error`
// prop would be evaluated once at create and frozen — v3.14); the control
// takes the danger edge through `class: { hasError: (el, s) => … }`.
//
// `ariaLabel` reads the SAME `label` string AppField's own FieldLabel renders
// visually, off `el.parent` — the cascade validated in catalog's
// CatalogInput/CatalogField fix. A control used outside an AppField (a bare
// filter/picker) keeps the name its call site gives it (`aria: { label }`):
// a function prop that returned undefined REMOVED a call-site `aria-label`
// (measured by the tickets migration, 2026-09-24), so `fieldName` below
// falls back to the control's own. It is a module-level helper, not a
// registered function: a harness that mounts the kit with a minimal
// function registry must still get the name.
// The accessible name of a kit control: the field's `label` when the control
// sits in an AppField, else the control's own `aria.label`; never undefined
// for a control that has a name of its own. `host` is the element whose
// PARENT may be the field: the control itself, or — for the bare input inside
// a kit group (AppInputPrefix, AppInputWithIcon) — the group.
//
// FRANK: the helper is NOT read as a free variable — an arrow `ariaLabel` has
// no element `this`, so a published app read frank's lifted `fieldName` off
// `{}` and threw (frank-audit, crm-w1's run). It sits on each control's own
// `scope` (spread in at module evaluation, so frank serializes the function
// itself) and is read as `el.scope.fieldName`
// (__tests__/fieldKitNameSurvivesFrank.test.js).
const FIELD_NAME_SCOPE = {
  fieldName(el, host) {
    const h = host || el
    const field = h && h.parent
    const own = field && field.label
    if (own) return typeof own === 'function' ? own(field, field.state) : own
    const bag = el && el.aria && el.aria.label
    if (bag) return typeof bag === 'function' ? bag(el, el.state) : bag
    return undefined
  }
}

// FIELD-KIT DIRTY (perf/shell-keepalive, Nika 2026-10-10 "Yes, the field
// kit"). A user's input marks the kit control unsaved, so every shell form
// declares its unsaved work with no per-page code: the control carries
// `dirty` — the declaration the shell's `hasUnsavedInput` reads (the PageSlot
// keep-alive cap and the split-pane replace ask "Discard changes?" for it).
//   • marked   by the USER only: `beforeinput` on a text control (typing,
//              paste, delete — never a programmatic write) and `input` on a
//              select. The value the control showed before that first edit is
//              its LOADED value;
//   • cleared  when the value returns to the loaded one (computed on read,
//              through the kit's own change path), and when the page's save
//              succeeds: the shell's `markPageSaved` (el.call) takes every
//              kit control in the page as saved — a failed save does not call
//              it, so the controls stay unsaved.
// Per-control state lives on the control element (`__kitBase`,
// `__kitTouched`); the helpers sit on the control's own `scope` like
// FIELD_NAME_SCOPE (frank: no free variable — fieldKitNameSurvivesFrank).
const FIELD_DIRTY_SCOPE = {
  kitValue(el) {
    const node = el && el.node
    if (!node) return ''
    const v = node.value
    return v === undefined || v === null ? '' : String(v)
  },
  kitEdit(el) {
    if (!el || !el.node) return
    if (el.__kitBase === undefined) el.__kitBase = el.scope.kitValue(el)
    el.__kitTouched = true
  },
  kitDirty(el) {
    if (!el || !el.__kitTouched || el.__kitBase === undefined) return false
    return el.scope.kitValue(el) !== el.__kitBase
  },
  kitSaved(el) {
    if (!el) return
    el.__kitBase = el.scope.kitValue(el)
    el.__kitTouched = false
  }
}

export const AppField = {
  // A <label>, so a click on the caption focuses the control (the brand
  // field was one; a div lost that, measured by the calendar migration).
  // The control's accessible NAME still comes from its own aria-label —
  // the label's text content would have carried the hint and the error too.
  tag: 'label',
  flow: 'y',
  gap: 'Y',
  label: '',
  hint: '',
  error: '',
  reserveError: false,
  FieldLabel: {
    extends: 'AppFieldLabel',
    text: (el) => (el.parent && el.parent.label) || ''
  },
  FieldHint: {
    extends: 'AppFieldHint',
    order: '2'
  },
  FieldError: {
    extends: 'AppFieldError',
    order: '3'
  }
}

// The kit CAPTION — the one definition of the small uppercase label: `Y1`
// (12.7px at the 17px base — the AI House label size; `Z3` is not a step of
// the shell's type scale), 500 (the weight law: labels are 500), `.06em`,
// caption ink. AppField renders it as its FieldLabel. A caption that heads a
// block with no control of its own (a read-only preview in a sheet) extends
// it by NAME — `extends: 'AppFieldLabel'` — never by the path
// `'AppField > FieldLabel'`, which has no key form for the frank audit
// (FA806). It carries no text: the caller brings its own. Uppercase is CSS,
// never JS — `text-transform` leaves Georgian Mkhedruli alone, where
// `toUpperCase()` would turn it into Mtavruli capitals.
export const AppFieldLabel = {
  tag: 'span',
  fontSize: 'Y1',
  fontWeight: '500',
  color: 'caption',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
}

// The hint line — caption ink, one size under the control's type. Empty by
// default and takes no space then.
export const AppFieldHint = {
  tag: 'span',
  fontSize: 'Y1',
  color: 'caption',
  text: (el) => (el.parent && el.parent.hint) || '',
  hide: (el) => !(el.parent && el.parent.hint)
}

// The inline error line — danger ink, announced. With `reserveError` on the
// field it keeps its one-line box while empty (visibility, not display), so a
// validation pass never moves the form (Rule 66).
export const AppFieldError = {
  tag: 'span',
  role: 'alert',
  fontSize: 'Y1',
  fontWeight: '500',
  color: 'dangerInk',
  minHeight: 'Z1',
  text: (el) => (el.parent && el.parent.error) || '',
  hide: (el) => !(el.parent && (el.parent.reserveError || el.parent.error)),
  visibility: (el) => (el.parent && el.parent.error ? 'visible' : 'hidden')
}

// The control surface. The brand `field` theme owns the fills and the ink in
// both schemes (rest, hover, focus, and the ::placeholder ink through
// `field-placeholder`); the hairline `line` edge is declared here because the
// framework's own field theme paints its borderColor equal to its fill (no
// visible edge) and the Input atom sets `border: none` — the edge is the one
// stroke the borderless law keeps for a field (DESIGN_LANGUAGE §2.5.4), and
// it steps to `inactive` under the pointer so a field acknowledges the cursor
// without a fill jump. Focus: THE global ring only (brand reset.js paints it
// on every <input>/<select> :focus-visible — §8.2); `outline: 'none'` is
// fine, the house ring is a box-shadow. Disabled: the `field-disabled` pair,
// never opacity (§8.1).
//
// THE ERROR EDGE WINS UNDER THE POINTER. The hover step is scoped to a field
// WITHOUT an error (`&:not(.hasError):hover`), so the hover rule and the
// `&.hasError` danger edge never match the same element. It was a bare
// `:hover`, which the atomic engine emits with a doubled class
// (`.x.x:hover`, 0,3,0 — its guard against theme resting rules) and which
// therefore beat `.x.hasError` (0,2,0): an errored field showed the grey
// `inactive` edge under the pointer. The scoped rule is still 0,3,0, so it
// still outranks a theme's resting rule; and with no shared element there is
// no tie for source order (which the engine's rule dedup makes
// non-deterministic) to decide. Same rule on every kit surface below.
export const AppInput = {
  tag: 'input',
  theme: 'field',
  minHeight: 'controlH',
  minWidth: '0',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: '- Z2',
  round: 'radiusControl',
  fontSize: 'Z2',
  // iOS FOCUS ZOOM (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): a focused field under
  // 16px zooms the page in iOS Safari. `Z2` is ~15.4px at a phone's root and
  // ~12.9px at the iPad-portrait one (the compact `Z` 12.8 / 11.2px, measured
  // on dev DFI): on a touch screen the field text takes `fieldTextTouch`.
  '@coarse': { fontSize: 'fieldTextTouch' },
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  outline: 'none',
  transition: 'A defaultBezier',
  transitionProperty: 'background-color, border-color',
  scope: { ...FIELD_NAME_SCOPE, ...FIELD_DIRTY_SCOPE },
  ariaLabel: (el) => el.scope.fieldName(el),
  // unsaved work (FIELD-KIT DIRTY above)
  dirty: (el) => el.scope.kitDirty(el),
  onBeforeinput: (e, el) => el.scope.kitEdit(el),
  '&:not(.hasError):hover': { borderColor: 'inactive' },
  ':disabled': { theme: 'field-disabled', cursor: 'not-allowed' },
  '&.hasError': { borderColor: 'dangerInk' }
}

export const AppSelect = {
  tag: 'select',
  theme: 'field',
  minHeight: 'controlH',
  minWidth: '0',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: '- Z2',
  round: 'radiusControl',
  fontSize: 'Z2',
  // iOS FOCUS ZOOM (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): a focused field under
  // 16px zooms the page in iOS Safari. `Z2` is ~15.4px at a phone's root and
  // ~12.9px at the iPad-portrait one (the compact `Z` 12.8 / 11.2px, measured
  // on dev DFI): on a touch screen the field text takes `fieldTextTouch`.
  '@coarse': { fontSize: 'fieldTextTouch' },
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  outline: 'none',
  cursor: 'pointer',
  textOverflow: 'ellipsis',
  transition: 'A defaultBezier',
  transitionProperty: 'background-color, border-color',
  scope: { ...FIELD_NAME_SCOPE, ...FIELD_DIRTY_SCOPE },
  ariaLabel: (el) => el.scope.fieldName(el),
  // unsaved work (FIELD-KIT DIRTY above): a select has no `beforeinput`; its
  // loaded value is taken at focus, before the user's pick lands
  dirty: (el) => el.scope.kitDirty(el),
  onFocus: (e, el) => {
    if (el.__kitBase === undefined) el.__kitBase = el.scope.kitValue(el)
  },
  onInput: (e, el) => el.scope.kitEdit(el),
  '&:not(.hasError):hover': { borderColor: 'inactive' },
  // The browser paints a disabled <select> at opacity 0.7 (measured live on
  // dev, 2026-09-24): that dimmed the field-disabled pair to a see-through
  // control. The disabled law is the token step alone, so opacity stays 1.
  ':disabled': { theme: 'field-disabled', opacity: '1', cursor: 'not-allowed' },
  '&.hasError': { borderColor: 'dangerInk' }
}

// The multi-line control — the same surface, a vertical inset, and a
// vertical-only resize handle so a paragraph field never breaks its column.
export const AppTextarea = {
  tag: 'textarea',
  theme: 'field',
  minHeight: 'controlH',
  minWidth: '0',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: 'Z Z2',
  round: 'radiusControl',
  fontSize: 'Z2',
  // iOS FOCUS ZOOM (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): a focused field under
  // 16px zooms the page in iOS Safari. `Z2` is ~15.4px at a phone's root and
  // ~12.9px at the iPad-portrait one (the compact `Z` 12.8 / 11.2px, measured
  // on dev DFI): on a touch screen the field text takes `fieldTextTouch`.
  '@coarse': { fontSize: 'fieldTextTouch' },
  lineHeight: '1.4',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  outline: 'none',
  resize: 'vertical',
  transition: 'A defaultBezier',
  transitionProperty: 'background-color, border-color',
  scope: { ...FIELD_NAME_SCOPE, ...FIELD_DIRTY_SCOPE },
  ariaLabel: (el) => el.scope.fieldName(el),
  // unsaved work (FIELD-KIT DIRTY above)
  dirty: (el) => el.scope.kitDirty(el),
  onBeforeinput: (e, el) => el.scope.kitEdit(el),
  '&:not(.hasError):hover': { borderColor: 'inactive' },
  ':disabled': { theme: 'field-disabled', cursor: 'not-allowed' },
  '&.hasError': { borderColor: 'dangerInk' }
}

// The COMPACT controls — the same surface one step denser, for a dense row
// (a drawer's add-variable row, a table's inline editor): `controlHCompact`
// (36px) tall, `Z` type and the `- Z` inset that type calls for — the AI House
// compact pair (AhInlineSelectCompact). They change the size triple only; the
// fill, the edge, the states and the name come from the base. Rule 68: a dense
// row reaches for these; a call site never shrinks a control's own padding.
export const AppInputCompact = {
  extends: 'AppInput',
  minHeight: 'controlHCompact',
  padding: '- Z',
  fontSize: 'Z'
}

export const AppSelectCompact = {
  extends: 'AppSelect',
  minHeight: 'controlHCompact',
  padding: '- Z',
  fontSize: 'Z'
}

// ── Input groups ─────────────────────────────────────────────────────────────
// A fixed adornment and a bare input on ONE field surface: one edge, one hover
// step, one focus ring. The GROUP carries the control recipe (the brand
// `field` fill, the hairline `line` edge that steps to `inactive` under the
// pointer, `controlH`, `radiusControl`, `Z2` type, the `- Z2` inset); the input
// inside is AppInputBare, which paints nothing of its own. The states:
//   focus     the group takes THE ring (the `focusRing` shadow, §8.2) while
//             its input is focus-visible, and the input cancels the ring the
//             brand reset paints on every `input:focus-visible` — on a
//             transparent input inside a field that ring is a second box
//             (the move InviteFormInDropdown made first)
//   disabled  the group takes the `field-disabled` pair while its input is
//             disabled (the input keeps the not-allowed cursor)
//   error     `class: { hasError: (el, s) => … }` on the GROUP → the danger
//             edge, exactly as on AppInput
// A press on the group's own box — its inset, or an adornment, which ignores
// the pointer — moves focus to the input without a blur in between. A child
// a consumer appends (a status word, a clear button) keeps its own pointer
// and its own space. The group is a <div>, not a <label>: an AppField (a
// <label>) holds it, and labels do not nest.
export const AppInputGroup = {
  tag: 'div',
  flow: 'x',
  align: 'center',
  gap: 'Z',
  theme: 'field',
  minHeight: 'controlH',
  minWidth: '0',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: '- Z2',
  round: 'radiusControl',
  fontSize: 'Z2',
  // The group's bare input inherits this size: the touch floor is the group's
  // (see AppInput — DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1).
  '@coarse': { fontSize: 'fieldTextTouch' },
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'text',
  transition: 'A defaultBezier',
  transitionProperty: 'background-color, border-color',
  '&:not(.hasError):hover': { borderColor: 'inactive' },
  ':has(> input:focus-visible)': { shadow: 'focusRing' },
  ':has(> input:disabled)': { theme: 'field-disabled', cursor: 'not-allowed' },
  '&.hasError': { borderColor: 'dangerInk' },
  onMousedown: (e, el) => {
    if (e.target !== el.node) return
    const input = el.AppInputBare && el.AppInputBare.node
    if (!input || typeof input.focus !== 'function') return
    e.preventDefault()
    input.focus()
  }
}

// The text slot inside a kit group: no fill, no edge, no inset and no ring of
// its own — the group is the field. The reset's `font: inherit` and
// `color: inherit` for form controls give it the group's type and ink (the
// disabled ink too); the brand reset's `::placeholder` gives it the quiet
// placeholder ink (D-6). Its name: the label of the AppField that holds the
// GROUP, else its own `aria.label`; a caption-less group names its input with
// a flat `ariaLabel`, the one form that replaces this function.
export const AppInputBare = {
  tag: 'input',
  flex: '1',
  minWidth: '0',
  alignSelf: 'stretch',
  padding: '0',
  border: 'none',
  background: 'transparent',
  outline: 'none',
  textOverflow: 'ellipsis',
  scope: { ...FIELD_NAME_SCOPE, ...FIELD_DIRTY_SCOPE },
  ariaLabel: (el) => el.scope.fieldName(el, el.parent),
  // unsaved work (FIELD-KIT DIRTY above)
  dirty: (el) => el.scope.kitDirty(el),
  onBeforeinput: (e, el) => el.scope.kitEdit(el),
  ':focus-visible': { shadow: 'none' },
  ':disabled': { cursor: 'not-allowed' }
}

// The PREFIX group — a fixed text before the input: a host before a slug
// (`my.symbols.app/` + `acme`), a sign before an amount. `prefixText` is a
// string or a function of the group; the name is not `prefix`, an RDFa global
// attribute that would land on the node. The prefix sits flush against the
// typed text, so the pair reads as one value; it wears the caption ink and
// ignores the pointer. It stays in the accessibility tree: it is part of what
// the field says.
export const AppInputPrefix = {
  extends: 'AppInputGroup',
  gap: '0',
  prefixText: '',
  Prefix: {
    tag: 'span',
    flexShrink: '0',
    whiteSpace: 'nowrap',
    color: 'caption',
    pointerEvents: 'none',
    text: (el) => (el.parent && el.parent.prefixText) || ''
  },
  AppInputBare: {}
}

// The LEADING-ICON group — a glyph before the input: a search field's
// magnifier. `icon` names a brand sprite icon (default `search`). The glyph is
// decoration: hidden from assistive technology, in caption ink, the Icon
// atom's own one-em box (so it follows the control's type size), and it
// ignores the pointer.
export const AppInputWithIcon = {
  extends: 'AppInputGroup',
  icon: 'search',
  Icon: {
    name: (el) => (el.parent && el.parent.icon) || 'search',
    flexShrink: '0',
    color: 'caption',
    pointerEvents: 'none',
    'aria-hidden': 'true'
  },
  AppInputBare: {}
}

// The prefix FIELD: the AppField caption above a prefix group, the group's
// input named by that caption. A click on the caption or on the prefix
// focuses the input (AppField is a <label>). Usage:
//
//   UrlField: {
//     extends: 'AppFieldPrefix',
//     label: (el) => el.call('polyglot', 'workspace.createOrg.urlLabel'),
//     prefixText: 'my.symbols.app/',
//     AppInputPrefix: { AppInputBare: { value: …, onInput: … } }
//   }
export const AppFieldPrefix = {
  extends: 'AppField',
  prefixText: '',
  AppInputPrefix: {
    prefixText: (el) => el.parent && el.parent.prefixText
  }
}

// Bare <option> base for data-driven select collections
// (childExtends: 'AppOption' + children items { value, text, selected }).
export const AppOption = {
  tag: 'option'
}

// Canonical field-control recipe — collapses the byte-for-byte
// `extends: 'AppInput'|'AppSelect', theme: 'field', minHeight: 'controlH',
// padding: '0 A', borderWidth: 'hairline', fontSize: 'Z2', outline: 'none',
// boxSizing: 'border-box'` object every natali app hand-copied
// (CmsInput.js:22-33, HrInput.js:12-21, CrmInput.js:17-30, CapTableInput.js,
// JobInput.js, ComInput.js, SchInput.js, WhInput.js, HrSelect.js, CrmSelect.js,
// JobSelect.js, ComSelect.js, SchSelect.js, CapTableSelect.js, WhSelect.js,
// BiSelect.js — tickets/workspace.md's 2026-08-08 Fable review). `round`
// and (for Select) `cursor: 'pointer'` are NOT redeclared here — AppInput/
// AppSelect already carry the compliant `radiusControl`/`'pointer'` values
// (fill-only, border-habit sweep — tickets/opus.md), so they pass through
// unshadowed exactly like the already-minimal CmsInput/HrInput/CapTableInput
// forks. Per-app deltas (padding overrides, `width`, `round`/border
// redeclarations that differ from the base, `childExtends`, per-field
// `ariaLabel`, …) stay on the app's own one-line-extends fork — this
// component only owns the part that was truly identical everywhere.
// Since the 2026-09-24 field kit AppInput / AppSelect carry the whole
// recipe themselves; these two names stay for the forks that extend them and
// add nothing — one recipe, one place.
export const AppFieldInput = {
  extends: 'AppInput'
}

export const AppFieldSelect = {
  extends: 'AppSelect'
}

// Capsule button family — collapses the byte-for-byte ghost/primary/danger
// capsule recipe hand-copied across CmsToolbarBtn.js:22-84 and its siblings
// (HrToolbarBtn.js/HrPrimaryBtn.js, CrmToolbarBtn.js/CrmPrimaryBtn.js,
// CapTableInput.js's Toolbar/Primary/DangerBtn, JobToolbarBtn.js/
// JobPrimaryBtn.js, ComToolbarBtn.js/ComPrimaryBtn.js, SchToolbarBtn.js/
// SchPrimaryBtn.js, WhInput.js's Toolbar/Primary/DangerBtn,
// CatalogToolbarBtn.js). `AppCapsuleBtn` owns the traits that are identical
// across every fork: fixed-px `controlH`/`radiusPill` control geometry
// (workspace/DESIGN_LANGUAGE.md §6.2), the §8.1 disabled step (the brand
// `field-disabled` pair + not-allowed cursor — never opacity, 2026-09-24; it
// was opacity .55), and — white-label redesign 2026-09-26, DESIGN_SPEC §5.1 —
// the honest label weight (500; buttons are not titles), the press
// (`scale(0.97)` over `Z`), a default inset, and the COMPACT variant.
//
//   AppCapsuleBtnPrimary: { text: '…', compact: true }
//
// `compact: true` is the dense-row size — DESIGN_LANGUAGE §6.2's "Button
// compact" row: `controlHCompact`, `Z` type, a `Z2` inset — declared HERE,
// once. Rule 68: a toolbar never shrinks a control's
// own padding at the call site. It is published as `data-compact` and styled
// by a static rule, so a fork's own padding cannot out-rank it.
//
// The four roles: Primary (the ink capsule), Toolbar (tonal: the resting
// `layer` wash), Ghost (text only: "Back", "Previous step"), Danger (the
// danger ink on a wash of itself). Forks keep their own padding and
// transitions; the roles' fills and states live here.
export const AppCapsuleBtn = {
  extends: 'AppControl',
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'X',
  minHeight: 'controlH',
  // A block inset of Z: a one-line label (22.5 px at Z2) plus 2 × Z stays
  // under controlH, so a one-line capsule keeps its height; a label that
  // WRAPS (the text law below) keeps that inset instead of touching the
  // capsule's edges — measured on dev bad4186b3, CRM "new contact" at 390:
  // the Georgian primary wrapped to two lines in a 45 px capsule, 0 px above
  // and below them.
  padding: 'Z A',
  round: 'radiusPill',
  cursor: 'pointer',
  // THE TEXT LAW (overflow law §3b.8, DESIGN-WL-D2-KIT-OVERFLOW-LAW-1): a
  // label that meets a narrower container WRAPS inside the capsule — it was
  // `nowrap`, so a Georgian label spilled past its capsule in a 390 px
  // equal-grid foot. `break-word` (not `anywhere`) keeps the capsule's
  // min-content at its longest word, so it never folds into a tower: a
  // two-word label is at most two lines. AppLabelStack stays width-exact.
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  textAlign: 'center',
  fontSize: 'Z2',
  fontWeight: '500',
  transition: 'background-color A defaultBezier, transform Z defaultBezier',
  compact: false,
  // A flat hook — a call-site `data` object replaces a `data: {}` block
  // (FW-ELEMENT-DEFINITION-DATA-REPLACES-EXTENDS-DATA-1).
  dataCompact: (el) => !!el.compact,
  // THE COMPACT SIZE CARRIES ITS OWN TOUCH FLOOR, IN THIS BLOCK
  // (DESIGN-CAPSULEBTN-COARSE-FLOOR-NESTED-WRONG-1). The `'@coarse'` floor
  // used to sit in the `'@coarse'` block below, as an inner
  // `'&[data-compact]'`. It emits the same rule text either way — but not in
  // the same PLACE. Both rules are specificity (0,2,0): one class plus one
  // attribute, and a media block adds none. At equal specificity the cascade
  // falls back to SHEET ORDER, and the engine mints one compound class per
  // style BLOCK, once, where the first element that ever needs it mounts — so
  // the order of two blocks is decided by two unrelated elements' mounts,
  // while inside ONE block the flat body is always emitted immediately before
  // its own nested media rule. Split across two blocks the floor therefore
  // won on some pages and lost on others: measured on dev 7e8916816
  // (Validators, Finances, Tokens at 375-834) every compact capsule kept
  // `min-height: 36px` on a coarse pointer, so a finger 21px above or below
  // the centre missed it and the two vertical probes landed on the parent
  // Bar. Declared HERE the pair is atomic and no mount order can separate
  // them. Same reason AppControl states `controlHCompact` and its floor in
  // one block, and the shape NavbarRailControl and AppHeader already use.
  // The emitted cascade is resolved in __tests__/capsuleCoarseFloor.test.js —
  // a definition read cannot see sheet order.
  '&[data-compact]': {
    minHeight: 'controlHCompact',
    padding: 'Y Z2',
    fontSize: 'Z',
    '@coarse': { minHeight: 'controlH', minWidth: 'controlH' }
  },
  // A touch control occupies its full 44px box. No invisible hit area can
  // overlap a neighboring action or be clipped by a toolbar's scroll port.
  '@coarse': {
    position: 'relative',
    ...touchRow
  },
  ':active': { transform: 'scale(0.97)' },
  ':disabled': { theme: 'field-disabled', cursor: 'not-allowed', transform: 'none' }
}

// Tonal capsule — the resting `layer` wash (it was `overlay`, a 5% wash of
// the ground's own family that composites to ~1.01:1 on a white sheet), the
// `selected` step under the pointer and on press.
export const AppCapsuleBtnToolbar = {
  extends: 'AppCapsuleBtn',
  theme: 'layer',
  color: 'title',
  ':hover': { background: 'selected' },
  ':active': { background: 'selected' }
}

// The ink capsule — `theme: 'primary'` (its own hover / focus / pressed
// steps). A fork that paints a per-app fill sets its own theme.
export const AppCapsuleBtnPrimary = {
  extends: 'AppCapsuleBtn',
  theme: 'primary',
  border: 'none'
}

// Text-only capsule — the quiet second action beside a primary ("Back",
// "Cancel", "Previous step"; r029 r044): no fill at rest, the house washes
// under the pointer.
export const AppCapsuleBtnGhost = {
  extends: 'AppCapsuleBtn',
  border: 'none',
  background: 'transparent',
  color: 'title',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' }
}

// Destructive — the `dangerInk` pair (it was the raw brand `red` + a red
// hairline, ~2.4:1 in light) on a 12% wash of that same ink (r083 "Delete"):
// no border, and the wash follows the ink in both schemes. An inert control
// carries no hue: the base `:disabled` step replaces both.
// The wash mixes into `tintBase` (white / the card colour), not `transparent`,
// so the plate is opaque and the ink keeps >= 4.5:1 on any ground; the press
// step is 20% (24% over white read 4.45:1). designSystem/color.js tintBase.
export const AppCapsuleBtnDanger = {
  extends: 'AppCapsuleBtn',
  border: 'none',
  color: 'dangerInk',
  background: 'color-mix(in srgb, currentColor 12%, var(--color-tintBase))',
  ':hover': { background: 'color-mix(in srgb, currentColor 18%, var(--color-tintBase))' },
  ':active': { background: 'color-mix(in srgb, currentColor 20%, var(--color-tintBase))' }
}

// THE LABEL STACK — a capsule whose label changes (rest → armed → busy:
// "Delete" → "Confirm delete?" → "Deleting…") and must never RESIZE while it
// does (Rule 66). Every label is mounted in ONE grid cell and only the current
// one is visible, so the capsule is as wide as its LONGEST label in the
// active language from the first paint. A `minWidth` floor cannot do this:
// W2 measured "Confirm deactivation?" at 202 px in English and 294 px in
// Georgian, and every floor was either too narrow for Georgian or too wide
// for English. A hidden label (`visibility: hidden`) keeps its box and is out
// of the accessible name, so the button reads exactly its visible label.
//
//   AppCapsuleBtnDanger: {
//     AppLabelStack: {
//       labels: ['common.action.delete', 'shell.orgSwitcher.deleteConfirm',
//                'shell.orgSwitcher.deleting'],   // polyglot keys, in order
//       shown: (el, s) => (s._busy ? 2 : s._confirming ? 1 : 0)
//     }
//   }
//
// `labels` are polyglot keys (a plain string passes through). `shown` is the
// index of the visible label — a number, or a factory of the owner's state.
export const AppLabelStack = {
  tag: 'span',
  display: 'inline-grid',
  labels: [],
  shown: 0,
  children: (el) =>
    (Array.isArray(el.labels) ? el.labels : []).map((label, index) => ({
      key: 'label' + index,
      label,
      index
    })),
  childProps: {
    tag: 'span',
    gridArea: '1 / 1',
    justifySelf: 'center',
    whiteSpace: 'nowrap',
    text: (el) => el.call('polyglot', el.label || ''),
    visibility: (el, s) => {
      const stack = el.parent || {}
      const v = typeof stack.shown === 'function' ? stack.shown(stack, s) : stack.shown
      return el.index === (Number(v) || 0) ? 'visible' : 'hidden'
    }
  }
}

// The multi-line field under its kit name (the AppFieldInput / AppFieldSelect
// pattern): the same control as AppTextarea.
export const AppFieldTextarea = {
  extends: 'AppTextarea'
}

// THE SEARCH FIELD — compact, a capsule, the kit's `field` surface and its
// hairline edge, a leading magnifier, a CLEAR button in a reserved box, and a
// keycap hint when a shortcut is wired (DESIGN_SPEC §5.4, L07; r072 r015).
//
//   AppFieldSearch: {
//     placeholder: (el) => el.call('polyglot', 'crm.search.contacts'),
//     value: (el, s) => s.root.crmQuery || '',
//     onInput: (e, el, s) => s.root.update({ crmQuery: e.target.value }),
//     onClear: (e, el, s) => s.root.update({ crmQuery: '' }),
//     shortcut: '⌘ K'          // only when the shortcut is WIRED
//   }
//
// `onInput` rides the input's own event up to this group (input events
// bubble). The end of the field holds ONE affordance at a time: the keycaps
// while it is empty (only when a shortcut is wired), the Clear button once
// there is a value and the call site handles `onClear`. Without a shortcut
// the Clear keeps its box while hidden, so the first keystroke moves nothing;
// with one, the two trade places inside the field — its own width never
// changes, so nothing outside it moves either (Rule 66).
// The input is `type=search` (the browser's own cancel glyph is hidden —
// ours is the one clear). Its accessible name: the AppField label around it,
// else `aria.label` on the group, else the placeholder.
export const AppFieldSearch = {
  extends: 'AppInputWithIcon',
  icon: 'search',
  shortcut: '',
  // Exactly the compact class height, like the 36 px buttons and selects in
  // the same row. The Clear is the kit's 36 px square (the icon-button law),
  // so with `minHeight` alone the hairline border grew the field to 38 px;
  // the fixed height lets the square overlap the hairline instead, unclipped.
  // A call site that needs the 44 px field sets `minHeight: 'controlH'`.
  // The compact TYPE too: `Z`, the AppInputCompact / AppSelectCompact size
  // beside it in the same row (it read `Z1`, so the search text stood one
  // step larger than the selects' — 15.2 against 14.1 px at 1920 on dev
  // kestrel-assist /production, 2026-10-28).
  height: 'controlHCompact',
  minHeight: 'controlHCompact',
  padding: '- X - Z',
  gap: 'Y',
  fontSize: 'Z',
  round: 'radiusPill',
  role: 'search',

  AppInputBare: {
    type: 'search',
    autocomplete: 'off',
    placeholder: (el) => {
      const v = el.parent && el.parent.placeholder
      return typeof v === 'function' ? v(el.parent, el.parent.state) : v || null
    },
    value: (el) => {
      const v = el.parent && el.parent.value
      return (typeof v === 'function' ? v(el.parent, el.parent.state) : v) || ''
    },
    ariaKeyshortcuts: (el) => {
      const k = el.parent && el.parent.shortcut
      return k
        ? String(k)
            .trim()
            .split(/[\s+]+/)
            .join('+')
        : null
    },
    '::-webkit-search-cancel-button': { display: 'none' }
  },

  Shortcut: {
    flow: 'x',
    gap: 'W',
    flexShrink: '0',
    ariaHidden: 'true',
    pointerEvents: 'none',
    hide: (el) => {
      const p = el.parent || {}
      const v = typeof p.value === 'function' ? p.value(p, p.state) : p.value
      return !p.shortcut || !!v
    },
    childExtends: 'AppKeycap',
    children: (el) =>
      String((el.parent && el.parent.shortcut) || '')
        .trim()
        .split(/[\s+]+/)
        .filter(Boolean)
        .map((k, i) => ({ key: `k${i}`, text: k }))
  },

  // the clear button — the kit's icon-only square (CompactIconButton: the
  // icon-button law, DESIGN-WL-ICON-BUTTONS-SQUARE-1; the brand IconButton's
  // coarse-pointer tap slop comes with it). It was an `iconLg` box — a square,
  // but off the control scale the law names; a 36 px control fills the
  // compact field over its hairline, and sits inside a 44 px one.
  IconButton: {
    extends: 'CompactIconButton',
    type: 'button',
    theme: 'transparent',
    color: 'caption',
    ':hover': { background: 'hover', color: 'title' },
    ariaLabel: (el) => el.call('polyglot', 'common.action.clear'),
    Icon: { name: 'x', boxSize: 'iconSm' },
    // Takes no box while the keycaps hold the end of an empty field.
    hide: (el) => {
      const p = el.parent || {}
      const v = typeof p.value === 'function' ? p.value(p, p.state) : p.value
      return !!p.shortcut && !v
    },
    // Reserved: hidden (not removed) while there is nothing to clear, or
    // nothing to clear it with.
    visibility: (el) => {
      const p = el.parent || {}
      const v = typeof p.value === 'function' ? p.value(p, p.state) : p.value
      return v && typeof p.onClear === 'function' ? 'visible' : 'hidden'
    },
    tabindex: (el) => {
      const p = el.parent || {}
      const v = typeof p.value === 'function' ? p.value(p, p.state) : p.value
      return v && typeof p.onClear === 'function' ? '0' : '-1'
    },
    onClick: (e, el, s) => {
      const group = el.parent
      if (group && typeof group.onClear === 'function') group.onClear(e, group, s)
    }
  }
}
